import { CompetitionCreate, LeaderboardMetric } from '@app/graphql'
import { proxy } from 'valtio'
import {
  CompetitionAudience,
  CompetitionScoringType,
  CompetitionSportScope,
  UNLIMITED_MAX_PARTICIPANTS,
} from '@app/screens/Tournament/CreateTournament/constants'
import { CompetitionRewardDisplayMode } from '@app/screens/Tournament/CreateTournament/types'

export type SelectedCompetitionEvent = {
  id: string
  title: string
  bookmakerId?: string | null
  eventDate?: string | null
  homeName?: string
  awayName?: string
  /** League / tournament line under the match title (Add events sheet) */
  subtitle?: string | null
}

export type NewCompetitionDraft = Omit<CompetitionCreate, 'competitionType'> & {
  competitionType: CompetitionScoringType
  /** `null` = all countries eligible (omit on create). */
  eligibleCountryIds: string[] | null
  unlimitedParticipants: boolean
  audience: CompetitionAudience
  /** Step 2 — persisted when navigating between wizard steps */
  selectedEventDetails: SelectedCompetitionEvent[]
  step2SportId: string | null
  step2SportName: string | null
  step2MarketId: string | null
  step2MarketName: string | null
  rewardDisplayMode: CompetitionRewardDisplayMode
  /** Single graphic describing 1st–3rd prizes when mode is COVER_IMAGE */
  rewardsCoverImageUrl: string
  /** True while cover image is uploading after crop */
  coverImageUploading: boolean
  /** Step 2 (tipster) — ranking metric */
  rankingMetric: LeaderboardMetric
  sportScope: CompetitionSportScope
  rankingSportId: string | null
  rankingSportName: string | null
  minimumSettledTips: number
  /** When true, make the competition visible immediately on creation */
  publishWhenCreated: boolean
  /** Set when editing an existing competition (from ?editId=). */
  editingCompetitionId: string | null
}

const defaultRewards = [
  { title: 'Gold Reward', reward: '', description: '' },
  { title: 'Silver Reward', reward: '', description: '' },
  { title: 'Bronze Reward', reward: '', description: '' },
]

export const createEmptyCompetitionDraft = (): NewCompetitionDraft => ({
  bookmarkerId: '',
  channelIds: [],
  cta: '',
  description: '',
  endAt: null,
  startAt: null,
  imageUrl: '',
  events: [],
  marketIds: [],
  maxParticipants: 20,
  rewards: defaultRewards.map((r) => ({ ...r })),
  name: '',
  termsAndconditions: '',
  termsAndconditionsLink: '',
  vip: false,
  competitionType: CompetitionScoringType.VOTE_OUTCOME,
  eligibleCountryIds: null,
  unlimitedParticipants: false,
  audience: CompetitionAudience.USER,
  selectedEventDetails: [],
  step2SportId: null,
  step2SportName: null,
  step2MarketId: null,
  step2MarketName: null,
  rewardDisplayMode: CompetitionRewardDisplayMode.COVER_IMAGE,
  rewardsCoverImageUrl: '',
  coverImageUploading: false,
  rankingMetric: LeaderboardMetric.Profit,
  sportScope: CompetitionSportScope.ALL,
  rankingSportId: null,
  rankingSportName: null,
  minimumSettledTips: 5,
  publishWhenCreated: true,
  editingCompetitionId: null,
})

export const isTipsterPerformanceDraft = (draft: NewCompetitionDraft): boolean =>
  draft.audience === CompetitionAudience.TIPSTER

export const NewCompetitionState = proxy<NewCompetitionDraft>(createEmptyCompetitionDraft())

export const resetNewCompetitionState = (partial?: Partial<NewCompetitionDraft>) => {
  const next = { ...createEmptyCompetitionDraft(), ...partial }
  if (next.audience === CompetitionAudience.TIPSTER) {
    next.competitionType = CompetitionScoringType.TIPSTER_PERFORMANCE
  }
  Object.assign(NewCompetitionState, next)
}

export const resolveMaxParticipantsForApi = (draft: NewCompetitionDraft): number =>
  draft.unlimitedParticipants ? UNLIMITED_MAX_PARTICIPANTS : draft.maxParticipants
